"use client";

import { useEffect } from "react";

import { marketingAllowed } from "@/lib/privacyCookie";

/**
 * Browser-side Meta Pixel bootstrap, split into two phases so the heavy
 * `fbevents.js` fetch (~98 KiB, which itself pulls ~100 KiB of signals/config)
 * stays off the initial mobile load:
 *
 *   1. Synchronous: define the lightweight `fbq` **stub** + queue and queue
 *      `dataProcessingOptions(['LDU'])` + `fbq('init', …)`. This is the standard
 *      Meta snippet MINUS the `fbevents.js` <script> injection.
 *   2. Deferred (requestIdleCallback, 2s fallback — mirrors PostHogProvider):
 *      inject `<script async src="…/fbevents.js">`. When it loads it drains
 *      `fbq.queue`, so the queued init + any browser events fire then.
 *
 * Why this is safe: the event components (`FacebookPixelPageView` /
 * `…ViewContent` / `…CheckoutClicks`) guard on `window.fbq` and fire the
 * server-side CAPI from inside that guard. Because the stub exists immediately,
 * those events queue AND the server CAPI fires right away — Meta dedups the
 * browser↔server pair by the shared `eventID` whenever fbevents.js finishes
 * loading, so deferring the fetch doesn't cost attribution.
 *
 * Why inject rather than render a <script>: React 19 / Next 16 treat an inline
 * `<script>` rendered inside a component as inert on the client (and log a
 * console error). Creating the element in an effect executes reliably.
 *
 * Consent: `<FacebookPixel />` only checks `FB_PIXEL_ID` (an env gate, same
 * for everyone). The per-visitor opt-out is the `marketingAllowed()` check at
 * the top of the effect below — see that comment and
 * documentation/compliance.md for why it moved off the server.
 */
export function FacebookPixelInit({ pixelId }: { pixelId: string }) {
  useEffect(() => {
    // Consent gate. This used to be a server-side `cookies()` read in
    // `<FacebookPixel />`; it moved here so the HTML stays identical for every
    // visitor and can be cached at the edge. Returning early means no `fbq`
    // stub, no queue, and no `fbevents.js` request — nothing reaches Meta.
    // `marketingAllowed()` also honors a live GPC signal, which matters for a
    // first-time GPC visitor whose page came from the cache and therefore
    // never ran the proxy that would have written the cookie.
    if (!marketingAllowed()) return;

    const hasFbq = () =>
      Boolean((window as unknown as { fbq?: unknown }).fbq);

    // Phase 1 (synchronous): the `fbq` stub + queue + init, without fetching
    // fbevents.js. The IIFE guards against double-install (`if(f.fbq)return`).
    if (!hasFbq()) {
      const stub = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[]}(window, document,'script');
        fbq('dataProcessingOptions', ['LDU'], 0, 0);
        fbq('init', '${pixelId}');
      `;
      const el = document.createElement("script");
      el.id = "facebook-pixel-stub";
      el.textContent = stub;
      document.head.appendChild(el);
    }

    // Phase 2 (deferred): fetch fbevents.js off the critical path. Guarded by id
    // so a client navigation / Strict Mode remount doesn't append a second tag.
    const loadFbevents = () => {
      if (document.getElementById("facebook-pixel")) return;
      const el = document.createElement("script");
      el.id = "facebook-pixel";
      el.async = true;
      el.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.appendChild(el);
    };

    const canIdle = typeof window.requestIdleCallback === "function";
    const handle = canIdle
      ? window.requestIdleCallback(loadFbevents, { timeout: 2000 })
      : window.setTimeout(loadFbevents, 1500);

    return () => {
      if (canIdle && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(handle as number);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, [pixelId]);

  return null;
}

export default FacebookPixelInit;
